import { useEffect, useState, useMemo, useCallback } from 'react';
import type { ReactNode } from 'react';
import { convertIpfsUrl } from '../utils';

// Import lukso components on client side only
if (typeof window !== 'undefined') {
  import('@lukso/web-components');
}

type LuksoSize = '2x-small' | 'x-small' | 'small' | 'medium' | 'large' | 'x-large' | '2x-large';

interface LuksoProfileAvatarProps {
  /** Profile address for identicon generation */
  address: string;
  /** Profile picture URL (optional) */
  profileUrl?: string | null;
  /** Profile name (for accessibility, not displayed) */
  name?: string | null;
  /** Size variant */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /** Show identicon badge when profile pic exists */
  showIdenticon?: boolean;
  /** Additional CSS classes */
  className?: string;
}

// Map our size variants to LUKSO's size names
const sizeMap: Record<string, LuksoSize> = {
  'xs': '2x-small',
  'sm': 'x-small',
  'md': 'small',
  'lg': 'medium',
  'xl': 'large',
  '2xl': 'x-large',
};

/**
 * LUKSO Profile Avatar Component
 * 
 * Wrapper around the official lukso-profile web component.
 * 
 * Handles:
 * - Client-side only loading (web components)
 * - IPFS URL → HTTP gateway conversion
 * - Size mapping
 * - Identicon display with profile pictures
 */
export function LuksoProfileAvatar({
  address,
  profileUrl,
  size = 'md',
  showIdenticon = true,
  className = '',
}: LuksoProfileAvatarProps) {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const luksoSize = sizeMap[size] || 'small';

  // Always convert IPFS URLs to HTTP gateway URLs
  const resolvedProfileUrl = useMemo(() => {
    if (!profileUrl) return undefined;
    return convertIpfsUrl(profileUrl);
  }, [profileUrl]);

  // Show placeholder during SSR
  if (!isMounted) {
    return <div className={`rounded-full bg-gray-600 animate-pulse ${className}`} />;
  }

  const isDefaultAvatar = !resolvedProfileUrl;

  return (
    <span
      className={`avatar-wrapper ${isDefaultAvatar ? 'avatar-default' : ''} ${className}`.trim()}
      data-default={isDefaultAvatar ? 'true' : undefined}
    >
      <lukso-profile
        profile-url={resolvedProfileUrl}
        profile-address={address}
        has-identicon={showIdenticon}
        size={luksoSize}
      />
    </span>
  );
}

/**
 * Profile display with avatar and optional name
 */
export function ProfileDisplay({
  address,
  profileUrl,
  name,
  size = 'md',
  className = '',
  addressSuffix,
  showCopyAddress = false,
}: {
  address: string;
  profileUrl?: string | null;
  name?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  className?: string;
  addressSuffix?: ReactNode;
  /** When true, show a copy button after the address (copies full address) */
  showCopyAddress?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await navigator.clipboard.writeText(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [address]);

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <LuksoProfileAvatar
        address={address}
        profileUrl={profileUrl}
        name={name}
        size={size}
        showIdenticon={true}
      />
      {name && (
        <div className="flex flex-col">
          <span className="font-medium">{name}</span>
          <div className="inline-flex items-center gap-1.5 text-sm text-gray-500">
            <span>
              {address.slice(0, 6)}...{address.slice(-4)}
            </span>
            {showCopyAddress && (
              <button
                type="button"
                onClick={handleCopy}
                className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                title="Copy address"
                aria-label="Copy address"
              >
                {copied ? (
                  <svg className="w-3.5 h-3.5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2M9 7h9a2 2 0 012 2v9a2 2 0 01-2 2h-9a2 2 0 01-2-2V9a2 2 0 012-2z" />
                  </svg>
                )}
              </button>
            )}
            {addressSuffix}
          </div>
        </div>
      )}
      {!name && (
        <div className="inline-flex items-center gap-1.5">
          <span className="font-mono text-sm">
            {address.slice(0, 10)}...{address.slice(-8)}
          </span>
          {showCopyAddress && (
            <button
              type="button"
              onClick={handleCopy}
              className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title="Copy address"
              aria-label="Copy address"
            >
              {copied ? (
                <svg className="w-3.5 h-3.5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2M9 7h9a2 2 0 012 2v9a2 2 0 01-2 2h-9a2 2 0 01-2-2V9a2 2 0 012-2z" />
                </svg>
              )}
            </button>
          )}
          {addressSuffix}
        </div>
      )}
    </div>
  );
}
